import { replaceEqualDeep } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
/** The rows escape hatch subscribes only after it is read; Items-only owners stay isolated. */
export function useTrackedListRows(client, queryHash, selectRows, includeEntityUpdates) {
  const selectorRef = useRef(selectRows);
  useEffect(() => {
    selectorRef.current = selectRows;
  });
  const selection = useMemo(() => {
    let tracked = false;
    let snapshot = null;
    const getSnapshot = () => {
      if (tracked) snapshot = replaceEqualDeep(snapshot, selectorRef.current());
      return snapshot;
    };
    return {
      getSnapshot,
      track: () => {
        tracked = true;
        getSnapshot();
      },
      subscribe: notify => {
        let pending = false;
        let active = true;
        const release = client.getQueryCache().subscribe(event => {
          if (!tracked || pending || event.type !== 'removed' && (event.type !== 'updated' || event.action.type !== 'success')) return;
          if (!includeEntityUpdates && event.query.queryHash !== queryHash) return;
          pending = true;
          // A normalized snapshot can write hundreds of entity keys in one batch.
          queueMicrotask(() => {
            pending = false;
            if (!active) return;
            const before = snapshot;
            if (getSnapshot() !== before) notify();
          });
        });
        return () => {
          active = false;
          release();
        };
      }
    };
  }, [client, queryHash, includeEntityUpdates]);
  useSyncExternalStore(selection.subscribe, selection.getSnapshot, selection.getSnapshot);
  return selection.track;
}